import { z } from "zod";
import { assignToSchema } from "./task";

export const tenantRoleSchema = z.enum(["admin", "member"]);

export const agentAdminUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  profilePictureUrl: z.string().nullable().optional(),
});

export const agentAdminMemberSchema = z.object({
  userId: z.string(),
  role: z.string(),
  membershipKind: z.enum(["team", "assignee"]),
  hasLoggedIn: z.boolean(),
  user: agentAdminUserSchema,
});

export const agentAdminGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  memberCount: z.number().optional(),
});

export const agentAdminGroupDetailSchema = agentAdminGroupSchema.extend({
  members: z.array(
    z.object({
      userId: z.string(),
      user: agentAdminUserSchema,
    })
  ),
});

export const agentAdminTaskSummarySchema = z.object({
  id: z.string(),
  convexId: z.string(),
  status: z.string(),
  type: z.string().nullable(),
  name: z.string().nullable(),
  description: z.string().nullable(),
  validUntil: z.number(),
  createdAt: z.number(),
  threadPriority: z.string().nullable(),
  handledByUserId: z.string().nullable(),
});

export const inviteMemberBodySchema = z.object({
  email: z.string().email(),
  role: tenantRoleSchema.optional(),
});

export const updateMemberRoleBodySchema = z.object({
  role: tenantRoleSchema,
});

export const createGroupBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
});

export const updateGroupBodySchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
});

export const addGroupMemberBodySchema = z.object({
  userId: z
    .string()
    .min(1)
    .describe("Convex user id or member email."),
});

export const listTasksQuerySchema = z.object({
  statusFilter: z.enum(["all", "open", "handled", "expired"]).optional(),
  typeFilter: z.string().optional(),
  appFilter: z.string().optional(),
  sortField: z.enum(["type", "date", "status", "validUntil"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  cursor: z.string().optional(),
});

export const searchTasksQuerySchema = z.object({
  q: z.string().min(1),
  limit: z.coerce.number().int().positive().max(100).optional(),
});

export const assignTasksResultSchema = z.object({
  taskId: z.string(),
  success: z.boolean(),
  message: z.string().optional(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

export const assignTasksResponseSchema = z.object({
  results: z.array(assignTasksResultSchema),
});

export const assignTasksBodySchema = z.object({
  taskIds: z.array(z.string().min(1)).min(1).max(100),
  assignTo: assignToSchema.refine(
    (value) => (value.users?.length ?? 0) > 0 || (value.groups?.length ?? 0) > 0,
    { message: "assignTo needs at least one user email or group slug." }
  ),
});

export const agentAdminPlanLimitsSchema = z.object({
  tasksPerDay: z.number(),
  members: z.number(),
  apiKeys: z.number(),
  groups: z.number(),
  auditLogRetention: z.number(),
  rateLimitPerSecond: z.number(),
  taskRetentionDays: z.number(),
});

/** Workspace plan/seat/task usage for tenant-admin agents (no Stripe ids). */
export const agentAdminUsageSchema = z.object({
  workspace: z.string(),
  planName: z.string(),
  planType: z.string(),
  trialState: z.enum(["none", "active", "expired"]),
  daysRemaining: z.number().nullable(),
  isPaidPro: z.boolean(),
  subscriptionStatus: z.string().nullable(),
  tasksToday: z.number(),
  maxTasksPerDay: z.number().nullable(),
  tasksResetAt: z.number(),
  openTasks: z.number(),
  totalTasks: z.number(),
  seatsCurrent: z.number(),
  seatsMax: z.number().nullable(),
  groupsCurrent: z.number(),
  groupsMax: z.number().nullable(),
  apiKeysCurrent: z.number(),
  apiKeysMax: z.number().nullable(),
  rateLimitPerSecond: z.number(),
  limits: agentAdminPlanLimitsSchema,
});

export type AgentAdminMember = z.infer<typeof agentAdminMemberSchema>;
export type AgentAdminGroup = z.infer<typeof agentAdminGroupSchema>;
export type AgentAdminGroupDetail = z.infer<typeof agentAdminGroupDetailSchema>;
export type AgentAdminTaskSummary = z.infer<typeof agentAdminTaskSummarySchema>;
export type AgentAdminAssignTaskResult = z.infer<typeof assignTasksResultSchema>;
export type AgentAdminUsage = z.infer<typeof agentAdminUsageSchema>;
